import React, { FC, useEffect, useMemo, useState } from 'react'
import { Select } from 'antd'
import { useQuery } from '@apollo/client'
import { GetItemCategoriesForLookup } from '../../inventory/graphql/item-category.graphql'
import { ItemCategoryLevel } from '../../inventory/type'
import { ItemType } from '../../inventory/type/item'

interface Props {
  level: ItemCategoryLevel
  value?: number
  onChange: (next: number | undefined, title?: string) => void
  disabled?: boolean
  /**
   * Scopes the dropdown to categories that have at least one inventory_item
   * of the given product type at the given level. When set, the backend
   * filters server-side via the productType filter on ItemCategoryFindRequest.
   */
  productType?: ItemType
  /**
   * Restricts to children of this category id. Used by the cascade picker:
   * Subcategory targets filter to children of the picked Category, etc.
   */
  parentId?: number
  placeholder?: string
}

// Page size for the level lookup. The backend already filters by level (and
// optionally productType) so we only need enough rows to cover one level.
const PAGE_TAKE = 200

export const CategoryByLevelSelect: FC<Props> = ({
  level,
  value,
  onChange,
  disabled,
  productType,
  parentId,
  placeholder,
}) => {
  const [search, setSearch] = useState('')
  const [labelCache, setLabelCache] = useState<Record<number, string>>({})

  const { data, loading } = useQuery(GetItemCategoriesForLookup, {
    variables: {
      request: {
        take: PAGE_TAKE,
        skip: 0,
        level,
        productType,
        parentId,
      },
    },
    fetchPolicy: 'cache-and-network',
  })

  const allRows = useMemo(() => data?.itemCategoriesFind.data ?? [], [data])

  useEffect(() => {
    if (allRows.length === 0) return
    setLabelCache((prev) => {
      const next = { ...prev }
      for (const row of allRows) {
        if (row.title) next[row.id] = row.title
      }
      return next
    })
  }, [allRows])

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase()
    return allRows
      .filter((row) => {
        if (!term) return true
        return (row.title ?? '').toLowerCase().includes(term)
      })
      .map((row) => ({ value: row.id, label: row.title ?? `#${row.id}` }))
  }, [allRows, search])

  const options = useMemo(() => {
    if (value === undefined) return filteredOptions
    if (filteredOptions.some((o) => o.value === value)) return filteredOptions
    const cached = labelCache[value]
    return [{ value, label: cached ?? `#${value}` }, ...filteredOptions]
  }, [filteredOptions, value, labelCache])

  return (
    <Select
      showSearch
      allowClear
      placeholder={placeholder ?? level}
      loading={loading}
      disabled={disabled}
      value={value}
      filterOption={false}
      onSearch={setSearch}
      options={options}
      onChange={(nextValue) => {
        if (nextValue === undefined) {
          onChange(undefined, undefined)
          return
        }
        const title =
          allRows.find((row) => row.id === nextValue)?.title ??
          labelCache[nextValue]
        onChange(nextValue, title)
      }}
      style={{ width: '100%' }}
    />
  )
}
